import { useState } from 'react';
import CustomSelect from './CustomSelect';
import { CopyIcon, ChevronDownIcon } from './Icons';
import { TYPE_CONFIG, STATUS_OPTIONS, PRIORITY_CONFIG, RETRO_TITLE_COLORS } from '../constants';
import { buildUserOptions, getAllDescendants } from '../utils';
import ItemChildren from './shared/ItemChildren';

export default function SprintItemCard({ item, onStatusChange, onAssigneeChange, onChildStatusToggle, onClick, users, showPriority, breadcrumbMap, allItems, isRetro, statusOptions = STATUS_OPTIONS }) {
  const [expanded, setExpanded] = useState(false);
  const config = TYPE_CONFIG[item.type] || TYPE_CONFIG.task;
  const Icon = config.icon;
  const userOptions = buildUserOptions(users, 16);
  const isExpandable = item.type === 'feature' || item.type === 'epic';

  // Count children for expandable types
  const childCount = isExpandable && allItems
    ? allItems.filter((i) => i.parentId === item.id).length
    : 0;
  const allDescendants = isExpandable && allItems
    ? getAllDescendants(item.id, allItems)
    : [];
  const doneCount = allDescendants.filter((d) => d.status === 'done').length;

  const handleDragStart = (e) => {
    e.dataTransfer.setData('text/plain', item.id);
    e.dataTransfer.effectAllowed = 'move';

    const clone = e.currentTarget.cloneNode(true);
    clone.style.position = 'absolute';
    clone.style.top = '-9999px';
    clone.style.left = '-9999px';
    clone.style.width = `${e.currentTarget.offsetWidth}px`;
    clone.style.opacity = '1';
    clone.style.transform = 'rotate(2deg)';
    clone.style.boxShadow = '0 12px 36px rgba(0,0,0,0.5)';
    document.body.appendChild(clone);
    e.dataTransfer.setDragImage(clone, e.nativeEvent.offsetX, e.nativeEvent.offsetY);
    requestAnimationFrame(() => document.body.removeChild(clone));
    e.currentTarget.classList.add('sprint-item-card--dragging');
  };

  const handleDragEnd = (e) => {
    e.currentTarget.classList.remove('sprint-item-card--dragging');
  };

  const priorityColor = showPriority && item.priority ? PRIORITY_CONFIG[item.priority]?.color : null;
  const breadcrumb = breadcrumbMap && item.parentId ? breadcrumbMap[item.id] : null;

  if (isRetro) {
    return (
      <div
        className="sprint-item-card sprint-item-card--retro"
        onClick={() => onClick(item)}
        draggable
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <h4
          className="sprint-item-title"
          style={{ color: RETRO_TITLE_COLORS[item.status] || 'var(--white)' }}
        >
          {item.title}
        </h4>
        {item.description && (
          <p className="sprint-item-desc">{item.description}</p>
        )}
      </div>
    );
  }

  return (
    <div
      className={`sprint-item-card${priorityColor ? ' sprint-item-card--priority' : ''}${isExpandable ? ' sprint-item-card--feature' : ''}${expanded ? ' sprint-item-card--expanded' : ''}`}
      data-type={item.type}
      style={priorityColor ? {
        background: `${priorityColor}18`,
        borderColor: `${priorityColor}40`,
        borderLeftColor: priorityColor,
      } : undefined}
      onClick={() => onClick(item)}
      draggable
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      {breadcrumb && (
        <div className="sprint-item-card-breadcrumb">
          {breadcrumb}
        </div>
      )}
      <div className="sprint-item-card-header">
        <span className="sprint-item-type" style={{ color: config.color }}>
          <Icon size={16} /> {config.label}
        </span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          {isExpandable && childCount > 0 && (
            <span className="feature-progress-badge">
              {doneCount}/{allDescendants.length}
            </span>
          )}
          {showPriority && item.priority && (
            <span className="priority-badge" style={{ background: PRIORITY_CONFIG[item.priority]?.color }}>
              {PRIORITY_CONFIG[item.priority]?.label}
            </span>
          )}
          {item.readableId && (
            <span
              className="sprint-item-rid"
              title="Copy ID"
              onClick={(e) => {
                e.stopPropagation();
                navigator.clipboard.writeText(`${item.readableId}-${item.title}`);
              }}
            >
              {item.readableId} <CopyIcon size={10} />
            </span>
          )}
          {item.storyPoints != null && (
            <span className="sprint-item-points">{item.storyPoints}</span>
          )}
        </div>
      </div>

      <h4 className="sprint-item-title">{item.title}</h4>

      {item.description && (
        <p className="sprint-item-desc">{item.description}</p>
      )}

      <div className="sprint-item-card-footer">
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginLeft: 'auto' }}>
          {isExpandable && childCount > 0 && (
            <button
              className={`feature-expand-btn${expanded ? ' feature-expand-btn--open' : ''}`}
              onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
              title={expanded ? 'Collapse' : 'Expand children'}
            >
              <ChevronDownIcon size={16} />
            </button>
          )}
          <div style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
            <CustomSelect
              value={item.assigneeId || ''}
              onChange={(val) => onAssigneeChange(item.id, val)}
              options={userOptions}
              size="xs"
              compact
            />
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
            <CustomSelect
              value={item.status}
              onChange={(val) => onStatusChange(item.id, val)}
              options={statusOptions}
              size="xs"
              compact
            />
          </div>
        </div>
      </div>

      {isExpandable && expanded && allItems && (
        <div onClick={(e) => e.stopPropagation()}>
          <ItemChildren
            item={item}
            allItems={allItems}
            onChildStatusToggle={onChildStatusToggle}
          />
        </div>
      )}
    </div>
  );
}
